import React, { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  BarChart3,
  Brain,
  CheckCircle2,
  ChevronRight,
  Gamepad2,
  Info,
  Lightbulb,
  Lock,
  PlayCircle,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  Users,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  GAME_PLAYERS,
  SUPPORT_LEVEL_ORDER,
  getSupportLevelStyle,
  normalizeSupportLevel,
} from '../data/gameData';
import { ABILITY_ORDER, buildPlayerProgress, getAbilityStyle } from '../data/supportTracks';

const formatNumber = (value, decimals = 0) =>
  value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

function useCountUp(target, duration = 1100) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let frame;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);

  return value;
}

function AnimatedNumber({ value, decimals = 0 }) {
  const animated = useCountUp(value);
  return <>{formatNumber(animated, decimals)}</>;
}

const KpiCard = ({ icon: Icon, label, value, caption, gradient, delay, testId, valueClassName = '' }) => (
  <Card
    data-testid={testId}
    className="game-card-enter relative overflow-hidden rounded-3xl border-0 bg-white shadow-xl"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className={`absolute -right-8 -top-8 h-28 w-28 rounded-full bg-gradient-to-br ${gradient} opacity-10`} />
    <CardContent className="relative p-6">
      <div className="relative">
        <div
          className={`absolute right-0 top-0 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg`}
        >
          <Icon className="h-6 w-6" />
        </div>
        <p className="game-label pr-16 font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
        <p className={`game-kpi-value mt-3 font-bold text-slate-900 ${valueClassName}`}>{value}</p>
        <p className="game-caption mt-2 leading-snug text-slate-500">{caption}</p>
      </div>
    </CardContent>
  </Card>
);

const DonutChart = ({ data, total, mounted }) => {
  const radius = 74;
  const stroke = 20;
  const circumference = 2 * Math.PI * radius;
  let accumulated = 0;

  return (
    <div
      data-testid="game-donut"
      className="relative mx-auto w-full max-w-[280px]"
      role="img"
      aria-label="Jogadores por nível de suporte"
    >
      <svg viewBox="0 0 200 200" className="h-auto w-full -rotate-90">
        <circle cx="100" cy="100" r={radius} fill="none" stroke="#eef4f2" strokeWidth={stroke} />
        {data.map((item, index) => {
          const fraction = total > 0 ? item.players / total : 0;
          const segment = circumference * fraction;
          const dash = Math.max(segment - 7, 0);
          const offset = -accumulated;
          accumulated += segment;
          const style = getSupportLevelStyle(item.level);

          return (
            <circle
              key={item.level}
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke={style.color}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${mounted ? dash : 0} ${circumference}`}
              strokeDashoffset={offset}
              style={{
                transition: `stroke-dasharray 1.1s cubic-bezier(.22,1,.36,1) ${index * 140}ms`,
              }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="game-kpi-value text-slate-900">
          <AnimatedNumber value={total} />
        </span>
        <span className="game-caption text-slate-500">jogadores</span>
      </div>
    </div>
  );
};

const LevelBars = ({ data, average, mounted }) => {
  const max = Math.max(...data.map((item) => item.sessions), 1);
  const total = data.reduce((sum, item) => sum + item.sessions, 0);

  return (
    <div>
      <div className="flex h-64 items-stretch gap-4 sm:gap-7">
        {data.map((item, index) => {
          const style = getSupportLevelStyle(item.level);
          const height = (item.sessions / max) * 100;

          return (
            <div key={item.level} className="flex h-full flex-1 flex-col">
              <div className="game-chart-value mb-2 text-center text-slate-700">
                <AnimatedNumber value={item.sessions} />
              </div>
              <div className="flex flex-1 items-end">
                <div
                  className="relative w-full overflow-hidden rounded-t-2xl shadow-sm"
                  style={{
                    height: mounted ? `${height}%` : '0%',
                    background: `linear-gradient(180deg, ${style.color} 0%, ${style.color}cc 100%)`,
                    transition: `height .95s cubic-bezier(.22,1,.36,1) ${index * 130}ms`,
                  }}
                >
                  <div className="absolute inset-x-0 top-0 h-1/3 bg-white/20" />
                  <div
                    className="absolute inset-0 opacity-30"
                    style={{
                      backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,.45), transparent 50%)',
                    }}
                  />
                </div>
              </div>
              <div className="mt-3 text-center">
                <p className="game-chart-label font-semibold text-slate-700">{item.level}</p>
                <p className="game-chart-label text-slate-400">{item.players} jogadores</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-5 flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
        <span className="game-caption text-slate-500">Média geral por jogador</span>
        <span className="game-legend-value text-slate-900">
          <AnimatedNumber value={average} decimals={1} /> partidas
        </span>
      </div>

      <div className="mt-3">
        <p className="game-chart-label mb-2 text-slate-400">Participação no total de partidas</p>
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${Math.max(data.length, 1)}, minmax(0, 1fr))` }}
        >
          {data.map((item) => {
            const style = getSupportLevelStyle(item.level);
            const percentage = total > 0 ? (item.sessions / total) * 100 : 0;

            return (
              <div
                key={item.level}
                className="rounded-xl px-2.5 py-2 text-center"
                style={{ backgroundColor: style.soft }}
              >
                <p className="game-chart-label font-medium" style={{ color: style.text }}>
                  {item.level}
                </p>
                <p className="game-legend-value mt-0.5" style={{ color: style.text }}>
                  {formatNumber(percentage)}%
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Histogram = ({ buckets, mounted }) => {
  const max = Math.max(...buckets.map((bucket) => bucket.count), 1);

  return (
    <div className="flex h-56 items-end gap-2 sm:gap-3">
      {buckets.map((bucket, index) => (
        <div key={bucket.label} className="flex h-full flex-1 flex-col items-center">
          <span className="game-chart-value mb-2 text-slate-600">{bucket.count}</span>
          <div className="flex w-full flex-1 items-end">
            <div
              className="w-full rounded-t-xl"
              style={{
                height: mounted ? `${(bucket.count / max) * 100}%` : '0%',
                background: `linear-gradient(180deg, ${bucket.color}, ${bucket.color}bb)`,
                transition: `height .9s cubic-bezier(.22,1,.36,1) ${index * 90}ms`,
              }}
            />
          </div>
          <span className="game-chart-label mt-2 text-slate-500">{bucket.label}</span>
        </div>
      ))}
    </div>
  );
};

const TopPlayers = ({ players, mounted, onSelect }) => {
  const max = Math.max(...players.map((player) => player.partidas), 1);

  const handleKeyDown = (event, player) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect?.(player);
    }
  };

  return (
    <div className="space-y-2">
      {players.map((player, index) => {
        const style = getSupportLevelStyle(player.level);
        return (
          <div
            key={player.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect?.(player)}
            onKeyDown={(event) => handleKeyDown(event, player)}
            title={`Ver evolução de ${player.nome}`}
            className="group cursor-pointer rounded-xl px-1.5 py-1.5 transition-colors hover:bg-emerald-50/50 focus:outline-none focus:ring-2 focus:ring-emerald-200"
          >
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className="game-tiny flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-bold"
                  style={{ backgroundColor: style.soft, color: style.text }}
                >
                  {index + 1}
                </span>
                <span className="game-table-name truncate font-medium text-slate-700">{player.nome}</span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className="game-badge rounded-full px-2.5 py-1"
                  style={{ backgroundColor: style.soft, color: style.text }}
                >
                  {player.level}
                </span>
                <span className="game-chart-value w-8 text-right text-slate-900">{player.partidas}</span>
                <ChevronRight className="h-3.5 w-3.5 text-slate-300 transition-colors group-hover:text-emerald-600" />
              </div>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full"
                style={{
                  width: mounted ? `${(player.partidas / max) * 100}%` : '0%',
                  background: `linear-gradient(90deg, ${style.color}, ${style.color}aa)`,
                  transition: `width .9s cubic-bezier(.22,1,.36,1) ${index * 70}ms`,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ComparisonBar = ({ label, value, max, color, mounted, delay }) => (
  <div>
    <div className="mb-1.5 flex items-center justify-between">
      <span className="game-chart-label text-white/75">{label}</span>
      <span className="game-chart-value text-white">{formatNumber(value, 1)}</span>
    </div>
    <div className="h-2.5 overflow-hidden rounded-full bg-white/10">
      <div
        className="h-full rounded-full"
        style={{
          width: mounted ? `${Math.min((value / Math.max(max, 1)) * 100, 100)}%` : '0%',
          background: color,
          transition: `width 1s cubic-bezier(.22,1,.36,1) ${delay}ms`,
        }}
      />
    </div>
  </div>
);

const AbilityRanking = ({ abilities, totalSessions, mounted }) => {
  const top = abilities[0] || { ability: '—', sessions: 0 };
  const max = Math.max(...abilities.map((item) => item.sessions), 1);
  const topPercentage = totalSessions > 0 ? (top.sessions / totalSessions) * 100 : 0;

  return (
    <Card
      data-testid="game-abilities"
      className="game-card-enter rounded-3xl border-0 bg-white shadow-xl"
      style={{ animationDelay: '620ms' }}
    >
      <CardContent className="p-6">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <p className="game-card-title text-slate-900">Habilidades mais trabalhadas</p>
            <p className="game-caption mt-1 text-slate-500">
              Sessões acumuladas conforme a progressão nas casas 1–14
            </p>
          </div>
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-50 text-violet-700">
            <Brain className="h-5 w-5" />
          </span>
        </div>

        <div className="mb-5 flex items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-emerald-700 shadow-sm">
              <Lightbulb className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="game-label font-semibold uppercase tracking-[0.16em] text-emerald-700">
                Habilidade destaque
              </p>
              <p className="game-card-title mt-1 truncate text-slate-900">{top.ability}</p>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <p className="game-kpi-text text-slate-900">
              <AnimatedNumber value={top.sessions} />
            </p>
            <p className="game-chart-label text-slate-500">
              {formatNumber(topPercentage)}% do total
            </p>
          </div>
        </div>

        <div className="space-y-3.5">
          {abilities.map((item, index) => {
            const style = getAbilityStyle(item.ability);
            const percentage = totalSessions > 0 ? (item.sessions / totalSessions) * 100 : 0;

            return (
              <div key={item.ability}>
                <div className="mb-1.5 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="game-tiny flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-bold"
                      style={{ backgroundColor: style.soft, color: style.text }}
                    >
                      {index + 1}
                    </span>
                    <span className="game-table-name truncate font-medium text-slate-700">
                      {item.ability}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="game-chart-value text-slate-900">{item.sessions}</span>
                    <span className="game-chart-label w-9 text-right text-slate-400">
                      {formatNumber(percentage)}%
                    </span>
                  </div>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: mounted ? `${(item.sessions / max) * 100}%` : '0%',
                      background: `linear-gradient(90deg, ${style.color}, ${style.color}aa)`,
                      transition: `width .9s cubic-bezier(.22,1,.36,1) ${index * 80}ms`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex items-start gap-2 rounded-2xl bg-slate-50 px-4 py-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <p className="game-note leading-relaxed text-slate-500">
            Considera a trilha de cada nível de suporte. Cada casa possui 7 partidas e a progressão
            começa sempre na casa 1.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const PlayerEvolutionDialog = ({ player, progress, open, onOpenChange }) => {
  if (!player || !progress) return null;

  const levelStyle = getSupportLevelStyle(player.level);
  const initials = player.nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  const abilities = Object.entries(progress.abilityTotals)
    .map(([ability, sessions]) => ({ ability, sessions }))
    .sort((a, b) => b.sessions - a.sessions);
  const topAbility = abilities[0];
  const maxAbility = Math.max(...abilities.map((item) => item.sessions), 1);
  const currentHouse = progress.currentHouse;
  const nextHouse = progress.nextHouse;
  const trackCompleted = progress.trackCompleted;
  const activeHouse = currentHouse || (!trackCompleted ? nextHouse : null);

  const summary = [
    {
      label: 'Casas concluídas',
      value: `${progress.completedHouses}/14`,
      sub: 'da trilha',
    },
    {
      label: currentHouse ? 'Casa atual' : trackCompleted ? 'Trilha' : 'Próxima casa',
      value: activeHouse ? `Casa ${activeHouse.casa}` : 'Trilha completa',
      sub: activeHouse
        ? `${currentHouse ? currentHouse.habilidade : 'próxima'} · ${activeHouse.played} de ${activeHouse.total}`
        : '14 de 14',
    },
    {
      label: 'Partidas',
      value: formatNumber(player.partidas),
      sub: 'realizadas',
    },
    {
      label: 'Habilidade destaque',
      value: topAbility ? topAbility.ability : '—',
      sub: topAbility ? `${topAbility.sessions} sessões` : 'Sem dados',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="player-evolution-dialog"
        className="max-h-[92vh] max-w-5xl overflow-hidden rounded-3xl border-0 p-0"
      >
        <div className="flex max-h-[92vh] flex-col">
          <div className="border-b border-slate-100 bg-white/95 px-6 py-5">
            <DialogHeader>
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-500 text-lg font-bold text-white shadow-lg">
                {initials}
              </div>
              <div className="min-w-0 flex-1 pr-10">
                <DialogTitle className="game-card-title text-slate-900">{player.nome}</DialogTitle>
                <DialogDescription className="game-caption mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-slate-500">
                  <span
                    className="game-badge inline-flex rounded-full px-2.5 py-1"
                    style={{ backgroundColor: levelStyle.soft, color: levelStyle.text }}
                  >
                    {player.level}
                  </span>
                  <span>{player.partidas} partidas realizadas</span>
                  <span className="text-slate-300">•</span>
                  <span>{progress.completedHouses} de 14 casas concluídas</span>
                </DialogDescription>
              </div>
              </div>
            </DialogHeader>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {summary.map((item) => (
              <div key={item.label} className="rounded-2xl bg-slate-50 px-4 py-3">
                <p className="game-label font-semibold uppercase tracking-[0.14em] text-slate-400">
                  {item.label}
                </p>
                <p className="game-card-title mt-1.5 truncate text-slate-900">{item.value}</p>
                <p className="game-chart-label mt-1 text-slate-500">{item.sub}</p>
              </div>
            ))}
          </div>

          <section>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="game-card-title text-slate-900">Habilidades trabalhadas</p>
                <p className="game-caption mt-1 text-slate-500">Sessões por habilidade neste jogador</p>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-50 text-violet-700">
                <Brain className="h-5 w-5" />
              </span>
            </div>

            {abilities.length === 0 ? (
              <p className="game-note mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-slate-500">
                Nenhuma partida registrada para este jogador.
              </p>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {abilities.map((item) => {
                  const style = getAbilityStyle(item.ability);
                  const percentage =
                    player.partidas > 0 ? (item.sessions / player.partidas) * 100 : 0;

                  return (
                    <div
                      key={item.ability}
                      className="rounded-2xl px-4 py-3"
                      style={{ backgroundColor: style.soft }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="game-table-name font-medium" style={{ color: style.text }}>
                          {item.ability}
                        </span>
                        <span className="game-legend-value" style={{ color: style.text }}>
                          {item.sessions}
                        </span>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/70">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(item.sessions / maxAbility) * 100}%`,
                            backgroundColor: style.color,
                            transition: 'width .8s cubic-bezier(.22,1,.36,1)',
                          }}
                        />
                      </div>
                      <p className="game-chart-label mt-1.5" style={{ color: style.text, opacity: 0.7 }}>
                        {formatNumber(percentage)}% das partidas do jogador
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="game-card-title text-slate-900">Evolução nas casas</p>
                <p className="game-caption mt-1 text-slate-500">
                  Progressão da casa 1 à 14 · 7 partidas por casa
                </p>
              </div>
              {activeHouse && (
                <span className="game-badge rounded-full bg-emerald-700 px-3 py-1.5 text-white shadow-sm">
                  {currentHouse ? 'Casa atual' : 'Próxima casa'}: {activeHouse.casa}
                </span>
              )}
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {progress.houses.map((house) => {
                const style = getAbilityStyle(house.habilidade);
                const isCompleted = house.status === 'completed';
                const isCurrent = house.status === 'current';
                const isPending = house.status === 'pending';
                const isNext = !currentHouse && !trackCompleted && nextHouse?.casa === house.casa;
                const statusLabel = isCompleted
                  ? 'Concluída'
                  : isCurrent
                  ? 'Em andamento'
                  : isNext
                  ? 'Próxima'
                  : 'Não iniciada';

                return (
                  <div
                    key={house.casa}
                    className={`rounded-2xl border p-4 transition-all ${
                      isCurrent
                        ? 'border-emerald-300 bg-emerald-50/60 shadow-md ring-1 ring-emerald-200'
                        : isNext
                        ? 'border-emerald-200 bg-emerald-50/30'
                        : isCompleted
                        ? 'border-emerald-100 bg-white'
                        : 'border-slate-100 bg-slate-50/60'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className={`game-badge flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-bold ${
                            isPending && !isNext ? 'bg-slate-200 text-slate-500' : 'bg-emerald-700 text-white'
                          }`}
                        >
                          {house.casa}
                        </span>
                        <span
                          className="game-badge truncate rounded-full px-2.5 py-1"
                          style={{ backgroundColor: style.soft, color: style.text }}
                        >
                          {house.habilidade}
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5 text-slate-400">
                        {isCompleted ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        ) : isCurrent || isNext ? (
                          <PlayCircle className={`h-4 w-4 ${isCurrent ? 'text-emerald-600' : 'text-emerald-500'}`} />
                        ) : (
                          <Lock className="h-4 w-4 text-slate-300" />
                        )}
                        <span
                          className={`game-tiny ${
                            isCurrent ? 'text-emerald-700' : isNext ? 'text-emerald-600' : ''
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/80">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(house.played / house.total) * 100}%`,
                          backgroundColor: style.color,
                          transition: 'width .8s cubic-bezier(.22,1,.36,1)',
                        }}
                      />
                    </div>
                    <p className="game-chart-label mt-1.5 text-slate-500">
                      {house.played} de {house.total} partidas
                    </p>

                    <div className={`mt-2 flex flex-wrap gap-1.5 ${isPending ? 'opacity-60' : ''}`}>
                      {house.games.map((game) => (
                        <span
                          key={game.name}
                          className="game-tiny rounded-full px-2 py-0.5"
                          style={{
                            backgroundColor: isPending ? '#f1f5f9' : style.soft,
                            color: isPending ? '#94a3b8' : style.text,
                          }}
                          title={`${game.name}: ${game.played} de ${game.planned} partidas`}
                        >
                          {game.name} · {game.played}/{game.planned}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

              <div className="flex items-start gap-2 rounded-2xl bg-slate-50 px-4 py-3">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <p className="game-note leading-relaxed text-slate-500">
                  A evolução considera a ordem fixa das casas e a distribuição de jogos do nível de
                  suporte do jogador.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DashboardGame = () => {
  const [mounted, setMounted] = useState(false);
  const [levelFilter, setLevelFilter] = useState('Todos');
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 140);
    return () => clearTimeout(timer);
  }, []);

  const players = useMemo(
    () =>
      GAME_PLAYERS.map((player) => ({
        ...player,
        level: normalizeSupportLevel(player.nivelSuporte),
      })),
    []
  );

  const levelStats = useMemo(() => {
    const grouped = new Map();

    players.forEach((player) => {
      const current = grouped.get(player.level) || {
        level: player.level,
        players: 0,
        sessions: 0,
      };
      current.players += 1;
      current.sessions += player.partidas;
      grouped.set(player.level, current);
    });

    return Array.from(grouped.values()).sort(
      (a, b) => SUPPORT_LEVEL_ORDER.indexOf(a.level) - SUPPORT_LEVEL_ORDER.indexOf(b.level)
    );
  }, [players]);

  const totalPlayers = players.length;
  const totalSessions = players.reduce((sum, player) => sum + player.partidas, 0);
  const averageSessions = totalPlayers > 0 ? totalSessions / totalPlayers : 0;
  const maxSessions = Math.max(...players.map((player) => player.partidas), 1);

  const support1 = levelStats.find((item) => item.level === 'Suporte 1') || {
    players: 0,
    sessions: 0,
  };
  const otherLevels = levelStats.filter((item) => item.level !== 'Suporte 1');
  const otherPlayers = otherLevels.reduce((sum, item) => sum + item.players, 0);
  const otherSessions = otherLevels.reduce((sum, item) => sum + item.sessions, 0);
  const otherLabel = otherLevels.length === 1 ? otherLevels[0].level : 'Demais níveis';
  const otherLabelTitle = otherLevels.length === 1 ? otherLevels[0].level : 'demais níveis';
  const otherPlayersPhrase = otherLevels.length === 1 ? `no ${otherLabel}` : 'nos demais níveis';
  const otherProsePhrase = otherLevels.length === 1 ? `do ${otherLabel}` : 'dos demais níveis';
  const support1Average = support1.players > 0 ? support1.sessions / support1.players : 0;
  const otherAverage = otherPlayers > 0 ? otherSessions / otherPlayers : 0;
  const engagementRatio = support1Average > 0 ? otherAverage / support1Average : 0;

  const topLevel = [...levelStats].sort(
    (a, b) => b.sessions / Math.max(b.players, 1) - a.sessions / Math.max(a.players, 1)
  )[0];

  const rankedPlayers = useMemo(
    () => [...players].sort((a, b) => b.partidas - a.partidas),
    [players]
  );

  const filteredPlayers = useMemo(
    () =>
      levelFilter === 'Todos'
        ? rankedPlayers
        : rankedPlayers.filter((player) => player.level === levelFilter),
    [levelFilter, rankedPlayers]
  );

  const topPlayers = rankedPlayers.slice(0, 6);

  const playerProgress = useMemo(() => {
    const progressMap = new Map();
    players.forEach((player) => {
      progressMap.set(player.id, buildPlayerProgress(player.partidas, player.level));
    });
    return progressMap;
  }, [players]);

  const groupAbilities = useMemo(() => {
    const totals = {};
    ABILITY_ORDER.forEach((ability) => {
      totals[ability] = 0;
    });

    players.forEach((player) => {
      const progress = playerProgress.get(player.id);
      if (!progress) return;
      Object.entries(progress.abilityTotals).forEach(([ability, sessions]) => {
        totals[ability] = (totals[ability] || 0) + sessions;
      });
    });

    return ABILITY_ORDER.map((ability) => ({ ability, sessions: totals[ability] || 0 })).sort(
      (a, b) => b.sessions - a.sessions
    );
  }, [players, playerProgress]);

  const selectedProgress = selectedPlayer ? playerProgress.get(selectedPlayer.id) : null;

  const histogram = useMemo(() => {
    const buckets = [
      { label: '0–5', min: 0, max: 5, count: 0, color: '#f59e0b' },
      { label: '6–10', min: 6, max: 10, count: 0, color: '#fbbf24' },
      { label: '11–15', min: 11, max: 15, count: 0, color: '#34d399' },
      { label: '16–20', min: 16, max: 20, count: 0, color: '#10b981' },
      { label: '21–25', min: 21, max: 25, count: 0, color: '#0d9488' },
      { label: '26–30', min: 26, max: 30, count: 0, color: '#0f766e' },
    ];

    players.forEach((player) => {
      const bucket = buckets.find((item) => player.partidas >= item.min && player.partidas <= item.max);
      if (bucket) bucket.count += 1;
    });

    return buckets;
  }, [players]);

  const filterOptions = ['Todos', ...levelStats.map((item) => item.level)];

  return (
    <div data-testid="dashboard-game-page" className="space-y-6">
      {/* Hero */}
      <section className="game-card-enter relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#042f2e] via-emerald-900 to-teal-800 p-7 text-white shadow-2xl lg:p-10">
        <div
          className="absolute inset-0 opacity-80"
          style={{
            background:
              'radial-gradient(circle at 18% 18%, rgba(16,185,129,.38), transparent 42%), radial-gradient(circle at 82% 0%, rgba(45,212,191,.28), transparent 40%), radial-gradient(circle at 65% 110%, rgba(20,184,166,.22), transparent 45%)',
          }}
        />
        <div className="game-float-slow absolute -right-16 -top-20 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl" />
        <div className="game-float-slower absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-teal-300/20 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />

        <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <span className="game-pill inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 font-medium text-emerald-100 backdrop-blur">
                <Sparkles className="h-3.5 w-3.5" />
                Programa de treino cognitivo
              </span>
              <span className="game-pill inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-white/70 backdrop-blur">
                <Activity className="h-3.5 w-3.5" />
                25 jogadores monitorados
              </span>
            </div>

            <h1 className="game-hero-title font-bold tracking-tight text-white">
              Dashboard <span className="text-emerald-300">Game</span>
            </h1>
            <p className="game-hero-sub mt-3 max-w-2xl leading-relaxed text-emerald-50/75">
              Acompanhe a jornada cognitiva dos jogadores, o volume de jogos e as partidas realizadas
              em cada nível de suporte.
            </p>

            <div className="mt-7 grid max-w-xl grid-cols-3 gap-3">
              {[
                { label: 'Jogadores', value: totalPlayers },
                { label: 'Jogos / partidas', value: totalSessions },
                { label: 'Níveis ativos', value: levelStats.length },
              ].map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-2xl border border-white/10 bg-white/[0.07] px-4 py-3 backdrop-blur-sm"
                >
                  <p className="game-hero-metric text-white">
                    <AnimatedNumber value={metric.value} />
                  </p>
                  <p className="game-hero-metric-label mt-1 uppercase tracking-[0.12em] text-emerald-100/60">
                    {metric.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden shrink-0 items-center justify-center lg:flex">
            <div className="absolute h-40 w-40 animate-ping rounded-full bg-emerald-400/10" />
            <div className="relative flex h-36 w-36 items-center justify-center rounded-[2rem] border border-white/15 bg-white/10 shadow-2xl backdrop-blur-xl">
              <Gamepad2 className="h-16 w-16 text-emerald-200" strokeWidth={1.4} />
              <span className="absolute -right-2 -top-2 flex h-9 w-9 items-center justify-center rounded-full bg-amber-400 text-slate-900 shadow-lg">
                <Trophy className="h-5 w-5" />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={Users}
          label="Jogadores"
          value={<AnimatedNumber value={totalPlayers} />}
          caption={`${support1.players} no Suporte 1 · ${otherPlayers} ${otherPlayersPhrase}`}
          gradient="from-emerald-500 to-teal-600"
          delay={60}
          testId="game-kpi-players"
        />
        <KpiCard
          icon={Gamepad2}
          label="Jogos / partidas"
          value={<AnimatedNumber value={totalSessions} />}
          caption="Sessões realizadas no total"
          gradient="from-teal-500 to-cyan-600"
          delay={140}
          testId="game-kpi-sessions"
        />
        <KpiCard
          icon={TrendingUp}
          label="Média por jogador"
          value={<AnimatedNumber value={averageSessions} decimals={1} />}
          caption="Partidas realizadas por jogador"
          gradient="from-sky-500 to-blue-600"
          delay={220}
          testId="game-kpi-average"
        />
        <KpiCard
          icon={Trophy}
          label="Maior engajamento"
          value={topLevel ? topLevel.level : '—'}
          valueClassName="game-kpi-text"
          caption={
            topLevel
              ? `${formatNumber(topLevel.sessions / Math.max(topLevel.players, 1), 1)} partidas por jogador`
              : 'Sem dados'
          }
          gradient="from-amber-400 to-orange-500"
          delay={300}
          testId="game-kpi-top-level"
        />
      </div>

      {/* Players by level + sessions by level */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <Card
          className="game-card-enter rounded-3xl border-0 bg-white shadow-xl xl:col-span-5"
          style={{ animationDelay: '340ms' }}
        >
          <CardContent className="p-6">
            <div className="mb-6 flex items-start justify-between gap-3">
              <div>
                <p className="game-card-title text-slate-900">Jogadores por nível</p>
                <p className="game-caption mt-1 text-slate-500">Distribuição dos jogadores por nível de suporte</p>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
                <Users className="h-5 w-5" />
              </span>
            </div>

            <DonutChart data={levelStats} total={totalPlayers} mounted={mounted} />

            <div className="mt-6 space-y-2.5">
              {levelStats.map((item) => {
                const style = getSupportLevelStyle(item.level);
                const percentage = totalPlayers > 0 ? (item.players / totalPlayers) * 100 : 0;

                return (
                  <div
                    key={item.level}
                    className="flex items-center justify-between rounded-2xl px-3.5 py-2.5"
                    style={{ backgroundColor: style.soft }}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: style.color }} />
                      <span className="game-table-name font-medium" style={{ color: style.text }}>
                        {item.level}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="game-legend-value" style={{ color: style.text }}>
                        {item.players}
                      </span>
                      <span className="game-chart-label" style={{ color: style.text, opacity: 0.65 }}>
                        {formatNumber(percentage)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card
          className="game-card-enter rounded-3xl border-0 bg-white shadow-xl xl:col-span-7"
          style={{ animationDelay: '420ms' }}
        >
          <CardContent className="p-6">
            <div className="mb-6 flex items-start justify-between gap-3">
              <div>
                <p className="game-card-title text-slate-900">Jogos / partidas por nível</p>
                <p className="game-caption mt-1 text-slate-500">
                  Volume de sessões realizadas em cada nível de suporte
                </p>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                <BarChart3 className="h-5 w-5" />
              </span>
            </div>

            <LevelBars data={levelStats} average={averageSessions} mounted={mounted} />
          </CardContent>
        </Card>
      </div>

      {/* Histogram + top players */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card
          className="game-card-enter rounded-3xl border-0 bg-white shadow-xl"
          style={{ animationDelay: '500ms' }}
        >
          <CardContent className="p-6">
            <div className="mb-6 flex items-start justify-between gap-3">
              <div>
                <p className="game-card-title text-slate-900">Distribuição de partidas</p>
                <p className="game-caption mt-1 text-slate-500">
                  Quantidade de jogadores por faixa de partidas realizadas
                </p>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-50 text-sky-700">
                <Target className="h-5 w-5" />
              </span>
            </div>

            <Histogram buckets={histogram} mounted={mounted} />

            <div className="mt-5 flex items-start gap-2 rounded-2xl bg-slate-50 px-4 py-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <p className="game-note leading-relaxed text-slate-500">
                Jogadores de Suporte 1 concentram-se na faixa de 0 a 5 partidas, com exceções
                pontuais acima disso. No Suporte 3, a maior parte fica entre 10 e 30 partidas.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card
          className="game-card-enter rounded-3xl border-0 bg-white shadow-xl"
          style={{ animationDelay: '580ms' }}
        >
          <CardContent className="p-6">
            <div className="mb-6 flex items-start justify-between gap-3">
              <div>
                <p className="game-card-title text-slate-900">Top jogadores</p>
                <p className="game-caption mt-1 text-slate-500">
                  Maiores volumes · clique para ver a evolução
                </p>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                <Trophy className="h-5 w-5" />
              </span>
            </div>

            <TopPlayers players={topPlayers} mounted={mounted} onSelect={setSelectedPlayer} />
          </CardContent>
        </Card>
      </div>

      {/* Group abilities */}
      <AbilityRanking abilities={groupAbilities} totalSessions={totalSessions} mounted={mounted} />

      {/* Insight */}
      <section
        className="game-card-enter relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-emerald-950 to-teal-900 p-7 text-white shadow-2xl lg:p-9"
        style={{ animationDelay: '700ms' }}
      >
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-emerald-400/15 blur-3xl" />
        <div className="absolute -bottom-16 left-1/4 h-48 w-48 rounded-full bg-teal-300/10 blur-3xl" />
        <div className="relative grid gap-8 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <div className="mb-3 flex items-center gap-2 text-emerald-300">
              <Sparkles className="h-4 w-4" />
              <span className="game-label font-semibold uppercase tracking-[0.18em]">Insight</span>
            </div>
            <p className="game-card-title text-white">Suporte 1 x {otherLabelTitle}</p>
            <p className="game-note mt-3 max-w-2xl leading-relaxed text-emerald-50/70">
              Jogadores de Suporte 1 realizaram em média{' '}
              <strong className="text-amber-300">{formatNumber(support1Average, 1)} partidas</strong>, contra{' '}
              <strong className="text-emerald-300">{formatNumber(otherAverage, 1)} partidas</strong>{' '}
              {otherProsePhrase} — uma diferença de aproximadamente{' '}
              <strong className="text-white">{formatNumber(engagementRatio, 1)}x</strong>.
            </p>

            <div className="mt-6 space-y-4">
              <ComparisonBar
                label="Suporte 1"
                value={support1Average}
                max={Math.max(support1Average, otherAverage)}
                color="linear-gradient(90deg,#fbbf24,#f59e0b)"
                mounted={mounted}
                delay={250}
              />
              <ComparisonBar
                label={otherLabel}
                value={otherAverage}
                max={Math.max(support1Average, otherAverage)}
                color="linear-gradient(90deg,#34d399,#10b981)"
                mounted={mounted}
                delay={380}
              />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Suporte 1', value: `${formatNumber(support1Average, 1)}`, sub: 'partidas / jogador' },
                { label: otherLabel, value: `${formatNumber(otherAverage, 1)}`, sub: 'partidas / jogador' },
                { label: 'Total de partidas', value: formatNumber(totalSessions), sub: 'sessões realizadas' },
                { label: 'Jogadores', value: formatNumber(totalPlayers), sub: 'em acompanhamento' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-sm"
                >
                  <p className="game-hero-metric-label uppercase tracking-[0.12em] text-emerald-100/55">
                    {item.label}
                  </p>
                  <p className="game-insight-value mt-2 font-bold text-white">{item.value}</p>
                  <p className="game-chart-label mt-1 text-emerald-100/55">{item.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Full table */}
      <Card
        data-testid="game-players-table"
        className="game-card-enter rounded-3xl border-0 bg-white shadow-xl"
        style={{ animationDelay: '780ms' }}
      >
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="game-card-title text-slate-900">Jogadores e partidas</p>
              <p className="game-caption mt-1 text-slate-500">
                Acompanhamento de {totalPlayers} jogadores · clique para ver a evolução
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {filterOptions.map((option) => {
                const isActive = levelFilter === option;
                const count =
                  option === 'Todos'
                    ? totalPlayers
                    : levelStats.find((item) => item.level === option)?.players || 0;

                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setLevelFilter(option)}
                    className={`game-pill inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 font-medium transition-all ${
                      isActive
                        ? 'border-emerald-700 bg-emerald-700 text-white shadow-md'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-300 hover:text-emerald-700'
                    }`}
                  >
                    {option}
                    <span
                      className={`game-tiny rounded-full px-1.5 py-0.5 ${
                        isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5 overflow-hidden rounded-2xl border border-slate-100">
            <div className="max-h-[520px] overflow-auto">
              <table className="w-full min-w-[720px] border-collapse">
                <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
                  <tr>
                    <th className="game-chart-label px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-400">
                      #
                    </th>
                    <th className="game-chart-label px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-400">
                      Jogador
                    </th>
                    <th className="game-chart-label px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-400">
                      Nível de suporte
                    </th>
                    <th className="game-chart-label px-4 py-3 text-right font-semibold uppercase tracking-wider text-slate-400">
                      Partidas
                    </th>
                    <th className="game-chart-label px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-400">
                      Distribuição
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlayers.map((player, index) => {
                    const style = getSupportLevelStyle(player.level);

                    return (
                      <tr
                        key={player.id}
                        className="game-table-row group cursor-pointer border-t border-slate-100 hover:bg-emerald-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
                        onClick={() => setSelectedPlayer(player)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedPlayer(player);
                          }
                        }}
                        tabIndex={0}
                        title={`Ver evolução de ${player.nome}`}
                        aria-label={`Ver evolução de ${player.nome}`}
                      >
                        <td className="game-table-cell px-4 py-3 font-medium text-slate-400">
                          {String(index + 1).padStart(2, '0')}
                        </td>
                        <td className="game-table-cell px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="game-table-name font-medium text-slate-800">{player.nome}</span>
                            <ChevronRight className="h-3.5 w-3.5 text-slate-300 transition-colors group-hover:text-emerald-600" />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="game-badge inline-flex rounded-full px-2.5 py-1"
                            style={{ backgroundColor: style.soft, color: style.text }}
                          >
                            {player.level}
                          </span>
                        </td>
                        <td className="game-table-cell px-4 py-3 text-right font-semibold text-slate-800">
                          {player.partidas}
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-1.5 w-full min-w-[140px] overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: mounted ? `${(player.partidas / maxSessions) * 100}%` : '0%',
                                backgroundColor: style.color,
                                transition: `width .8s cubic-bezier(.22,1,.36,1) ${Math.min(
                                  index * 25,
                                  400
                                )}ms`,
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-slate-400">
            <Info className="h-4 w-4" />
            <p className="game-note">
              Indicadores consolidados a partir das sessões registradas no programa.
            </p>
          </div>
        </CardContent>
      </Card>

      <PlayerEvolutionDialog
        player={selectedPlayer}
        progress={selectedProgress}
        open={Boolean(selectedPlayer)}
        onOpenChange={(open) => {
          if (!open) setSelectedPlayer(null);
        }}
      />
    </div>
  );
};

export default DashboardGame;
